import { Localization } from "@viamap/viamap2-common";
import { DataCollector } from "src/dataCollector/DataCollector";
import { wfsEndpoints } from "src/dataCollector/WFSCollectorEndpoints";
import { Conflict, ConflictSearchParams } from "./ConflictSearchResults";
import { message } from "./Message";
import { feature, featureCollection, lineString, multiLineString, multiPoint, point } from "@turf/helpers";
import union from "@turf/union";
import buffer from "@turf/buffer";
import { LayerInfo } from "src/common/managers/Types";
import { booleanIntersects, center, intersect, lineIntersect, lineSegment, lineSplit } from "@turf/turf";
import { areaByUserPreference, lengthByUserPreference } from "src/managers/PlanarCalculationMethods";
import { MultiLineString } from "geojson";


export async function searchSingleLayerForConflicts(
   geom: any,
   endpoint: keyof typeof wfsEndpoints,
   featureType: string,
   options?: {
      whoAmI?: string,
      filter?: any,
      group?: any,
      signal?: AbortSignal,
   }
): Promise<any> {
   return await DataCollector.WFS.getFeatureIntersect(endpoint, featureType as keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"], geom as any, {
      whoami: wfsEndpoints[endpoint]?.["whoAmI"] ? (options?.whoAmI ?? undefined) : undefined,
      signal: options?.signal ?? undefined
   }).then((res) => {
      if (options?.filter) {
         let filtered = filterFeaturesWithMapFilter(res.features, options.filter);
         return {
            ...res,
            features: filtered,
            metadata: {
               ...res.metadata,
               numberMatched: filtered.length + "",
               numberReturned: filtered.length + ""
            }
         };
      } else {
         return res;
      }
   })
}

function featureMatchesMapFilter(feature: any, filter: any): boolean {
   let actualFilter = filter;
   if (Array.isArray(filter[0])) {
      actualFilter = ["all", ...filter];
   }
   switch (actualFilter[0]) {
      case "any": {
         return actualFilter.slice(1).some((f: any) => featureMatchesMapFilter(feature, f));
      }
      case "all": {
         return actualFilter.slice(1).every((f: any) => featureMatchesMapFilter(feature, f));
      }
      case "==": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] == value;
      }
      case "!=": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] != value;
      }
      case ">": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] > value;
      }
      case "<": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] < value;
      }
      case ">=": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] >= value;
      }
      case "<=": {
         let [_, [_get, field], value] = actualFilter;
         return feature.properties?.[field] <= value;
      }
      default:
         return true;
   }
}

function filterFeaturesWithMapFilter(features: any[], filter: any) {
   return features.filter(feature => featureMatchesMapFilter(feature, filter));
}

function calcMapGroupKey(feature: any, group: any) {
   let props: Record<string, any> = feature.properties ?? {};
   if (Array.isArray(group[0])) {
      group = ["all", ...group];
   }
   if (!Array.isArray(group)) {
      return group;
   }
   switch (group[0]) {
      case "get": {
         let [_get, field] = group;
         return props?.[calcMapGroupKey(feature, field)];
      }
      case "concat": {
         let [_concat, ...parts] = group;
         return parts.map(p => calcMapGroupKey(feature, p)).join("");
      }
      case "case": {
         let [_case, condition, ...cases] = group;
         for (let i = 0; i < cases.length - 1; i += 2) {
            let valcase = cases[i];
            let value = cases[i + 1];
            if (calcMapGroupKey(feature, valcase) == calcMapGroupKey(feature, condition)) {
               return calcMapGroupKey(feature, value);
            }
         }
         if (cases.length % 2 === 1) {
            return calcMapGroupKey(feature, cases[cases.length - 1]);
         }
         return null;
      }
      case "*": {
         let [_star, ...parts] = group;
         return parts.map(p => calcMapGroupKey(feature, p)).reduce((acc, val) => acc * val);
      }
      case "+": {
         let [_plus, ...parts] = group;
         return parts.map(p => calcMapGroupKey(feature, p)).reduce((acc, val) => acc + val);
      }
      case "-": {
         let [_minus, ...parts] = group;
         return parts.map(p => calcMapGroupKey(feature, p)).reduce((acc, val) => acc - val);
      }
      case "/": {
         let [_divide, ...parts] = group;
         return parts.map(p => calcMapGroupKey(feature, p)).reduce((acc, val) => acc / val);
      }
      case "%": {
         let [_mod, a, b] = group;
         return calcMapGroupKey(feature, a) % calcMapGroupKey(feature, b);
      }
      case "round": {
         let [_round, value] = group;
         return Math.round(calcMapGroupKey(feature, value));
      }
      default:
         return group;
   }
}

function groupFeaturesWithMapGroup(features: any[], group: any) {
   if (!group) {
      return { "undefined": features ?? [] };
   }
   return features.reduce((acc, feature) => {
      let key = calcMapGroupKey(feature, group);
      if (!acc[key]) {
         acc[key] = [];
      }
      acc[key].push(feature);
      return acc;
   }, {} as Record<string, any[]>);
}

export async function handleClickProfileSearch(dataLayers: LayerInfo[], searchParams: ConflictSearchParams, setConflicts: React.Dispatch<React.SetStateAction<Conflict[]>>, abortController?: AbortController) {
   let layers = searchParams.layers ?? []
   if (searchParams.pages) {
      layers = searchParams.pages.map(p => p.layers).flat();
   }
   let conflicts: Conflict[] = (layers).map((layer) => ({
      ...layer,
      searchGeom: null,
      conflict: null,
      intersect: null,
      conflictMeasurement: 0,
      intersectMeasurement: 0,
      searchGeomMeasurement: 0,

      progress: 0,
      overlap: 0,
      area: 0,
      err: "",
      matchedCount: -1
   }));
   let dataLayerConflicts: Conflict[] = (dataLayers).map((layer) => ({
      source: Localization.getText("Data layer"),
      layer: layer.datasetname,
      title: layer.datasetname,

      searchGeom: null,
      conflict: null,
      intersect: null,
      conflictMeasurement: 0,
      intersectMeasurement: 0,
      searchGeomMeasurement: 0,

      progress: 0,
      err: "",
      matchedCount: -1
   }));
   setConflicts(conflicts.concat(dataLayerConflicts));
   let geom = forceFeatureCollection(geomWithBuffer(searchParams.geom, searchParams.buffer));

   for (let i = 0; i < dataLayerConflicts.length; i++) {
      if (abortController?.signal.aborted) {
         continue;
      }
      let foundConflicts = findConflictsInDataLayer(geom, dataLayers[i]);
      dataLayerConflicts = dataLayerConflicts.map((conflict, index) => {
         if (index === i) {
            return {
               ...conflict,
               err: "",
               progress: 1,

               searchGeom: geom,
               conflict: foundConflicts,
               intersect: intersectionFeatureCollection(foundConflicts, geom),
               conflictMeasurement: calculateFeatureCollectionArea(foundConflicts),
               intersectMeasurement: calculateOverlapArea(foundConflicts, geom),
               type: typeOfFeatureCollection(foundConflicts),
               intersectType: findIntersectionType(foundConflicts, geom),
               matchedCount: foundConflicts?.features.length || 0
            };
         }
         return conflict;
      });
      setConflicts(conflicts ? conflicts.concat(dataLayerConflicts) : dataLayerConflicts);
   };


   let perSource = Object.groupBy(conflicts, c => c.source)
   let resultingConflicts: Conflict[][] = await Promise.all(Object.keys(perSource)!.map(async (source) => {
      let sourceLayers = perSource[source];
      if (!sourceLayers) {
         message.error(Localization.getFormattedText("No layers found for source {source}", { source }));
         return [] as Conflict[];
      }
      for (let i = 0; i < sourceLayers.length; i++) {
         if (abortController?.signal.aborted) {
            continue;
         }
         let layer = sourceLayers[i];
         let searchResult: WFSFeatureCollection | null = null;
         try {
            searchResult = await searchSingleLayerForConflicts(geom, layer.source as keyof typeof wfsEndpoints, layer.layer as string, {
               filter: layer.filter,
               signal: abortController?.signal ?? undefined,
            });
            if (!searchResult) {
               message.error(Localization.getFormattedText("No search result for layer {layer} in source {source}", { layer: layer.layer, source: layer.source }));
            }
            sourceLayers[i] = {
               ...sourceLayers[i],
               progress: 1,
               searchGeom: geom,
               conflict: searchResult,
               intersect: intersectionFeatureCollection(searchResult, geom),
               conflictMeasurement: calculateFeatureCollectionArea(searchResult),
               intersectMeasurement: calculateOverlapArea(searchResult, geom),
               matchedCount: parseInt(searchResult?.metadata?.numberMatched || "0") || (searchResult?.features && searchResult.features.length) || 0
            };
            if (layer.group) {
               let groups = groupFeaturesWithMapGroup(searchResult?.features || [], layer.group);
               for (let key in groups) {
                  if (!sourceLayers[i].groupedConflicts) sourceLayers[i].groupedConflicts = {};
                  sourceLayers[i].groupedConflicts![key] = {
                     conflict: featureCollection(groups[key]),
                     intersect: intersectionFeatureCollection(featureCollection(groups[key]), geom),
                     conflictMeasurement: calculateFeatureCollectionArea(featureCollection(groups[key])),
                     intersectMeasurement: calculateOverlapArea(featureCollection(groups[key]), geom),
                  };
               }
            }
         } catch (e) {
            sourceLayers[i] = {
               ...sourceLayers[i],
               conflict: null,
               progress: 1,
               err: e instanceof Error ? e.message : String(e),
               matchedCount: -1
            };
         }
         setConflicts((c) => {
            let newConflicts = c.map(conflict => {
               if (conflict.source === sourceLayers[i].source && conflict.layer === sourceLayers[i].layer) {
                  return sourceLayers[i];
               }
               return conflict;
            })
            return newConflicts;
         });
      }
      return sourceLayers;
   }));
   setConflicts(() => resultingConflicts.flat().concat(dataLayerConflicts));
}

type WFSFeatureCollection = ReturnType<typeof featureCollection> & { metadata?: { endpoint?: string; numberMatched?: string; numberReturned?: number } };
export function geomWithBuffer(geom: any, bufferDistance?: number) {
   if (!geom) return featureCollection([]);
   if (bufferDistance) {
      if (geom.features.length > 1) return union(buffer(geom, bufferDistance, { units: 'meters' }) as any);
      return buffer(geom, bufferDistance, { units: 'meters' });
   }
   return geom;
}

export function forceFeatureCollection(geom: any) {
   if (geom.type === "FeatureCollection") return geom as WFSFeatureCollection;
   return featureCollection(geom.features ?? [geom]);
}

export function findConflictsInDataLayer(geom: any, dataLayer: LayerInfo): GeoJSON.FeatureCollection | null {
   if (!geom) {
      return null;
   }
   if (!dataLayer.geoJson) {
      return null;
   }
   let conflicts = dataLayer.geoJson.features.filter((feature) => isOverlappingFeatureCollection(geom, feature));
   if (conflicts.length > 0) {
      return featureCollection(conflicts);
   }
   return null;
}

export function isOverlappingFeatureCollection(featureCollection: GeoJSON.FeatureCollection, featureTarget: GeoJSON.Feature): boolean {
   for (let feature of featureCollection.features) {
      if (isOverlappingFeature(feature, featureTarget)) {
         return true;
      }
   }
   return false
}

export function calculateOverlapArea(feature1: GeoJSON.FeatureCollection | null, feature2: GeoJSON.FeatureCollection | null) {
   if (!feature1 || !feature2) return 0;
   let inter = intersectionFeatureCollection(feature1, feature2);
   return calculateFeatureCollectionArea(inter);
}


export function calculateFeatureCollectionArea(geom: GeoJSON.FeatureCollection | null) {
   if (!geom) return 0;
   switch (geom.features[0]?.geometry?.type) {
      case "Polygon":
      case "MultiPolygon":
         return areaByUserPreference(geom);
      case "LineString":
      case "MultiLineString":
         return lengthByUserPreference(geom);
      default:
         return 0;
   }
}

export function typeOfFeatureCollection(fc: GeoJSON.FeatureCollection | null) {
   if (!fc || fc.features.length === 0) return null;
   return fc.features[0].geometry.type;
}
export function findIntersectionType(fc1: GeoJSON.FeatureCollection | null, fc2: GeoJSON.FeatureCollection | null) {
   if (!fc1 || !fc2 || fc1.features.length === 0 || fc2.features.length === 0) return null;
   let type1 = typeOfFeatureCollection(fc1);
   let type2 = typeOfFeatureCollection(fc2);
   if (!type1 || !type2) return null;
   return findIntersectionTypeOfGeometryType(type1, type2);
}

export function isOverlappingFeature(feature1: GeoJSON.Feature, feature2: GeoJSON.Feature): boolean {
   if (booleanIntersects(feature1, feature2)) {
      return true;
   }
   return false;
}

export function findIntersectionTypeOfGeometryType(feature1: GeoJSON.Geometry["type"], feature2: GeoJSON.Geometry["type"]) {
   let simpleTypes = {
      "MultiPoint": "Point",
      "Point": "Point",
      "MultiLineString": "LineString",
      "LineString": "LineString",
      "MultiPolygon": "Polygon",
      "Polygon": "Polygon"
   }
   let [type1, type2] = [simpleTypes[feature1], simpleTypes[feature2]];
   let orderedTypes = [
      "Point",
      "LineString",
      "Polygon",
   ];
   if (orderedTypes.indexOf(type1) <= orderedTypes.indexOf(type2)) {
      return type1;
   }
   return type2;
}

function intersectionPoint(ft1: GeoJSON.Geometry, ft2: GeoJSON.Geometry): GeoJSON.Feature | null {
   if (ft1.type === "GeometryCollection"
      || ft2.type === "GeometryCollection"
   ) {
      return null;
   }
   if (ft1.type === "Point" || ft2.type === "Point") {
      if (booleanIntersects(ft1, ft2)) {
         if (ft1.type === "Point") {
            return feature(ft1);
         } else {
            return feature(ft2);
         }
      }
      return null;
   }
   if (ft1.type === "MultiPoint" || ft2.type === "MultiPoint") {
      if (booleanIntersects(ft1, ft2)) {
         if (ft1.type === "MultiPoint") {
            let intersectionPoints = ft1.coordinates.filter(pt => booleanIntersects(point(pt).geometry, ft2));
            if (intersectionPoints.length > 0) {
               return multiPoint(intersectionPoints);
            } else {
               return null;
            }
         } else {
            return intersectionPoint(ft2, ft1);
         }
      }
      return null;
   }
   return null;
}

function intersectionLine(ft1: GeoJSON.Geometry, ft2: GeoJSON.Geometry): GeoJSON.Feature | null {
   if (ft1.type === "Point"
      || ft2.type === "Point"
      || ft1.type === "MultiPoint"
      || ft2.type === "MultiPoint"
      || ft1.type === "GeometryCollection"
      || ft2.type === "GeometryCollection"
   ) {
      return intersectionPoint(ft1, ft2);
   }
   if (ft1.type === "LineString" && ft2.type === "LineString") {
      if (booleanIntersects(ft1, ft2)) {
         return multiPoint(lineIntersect(ft1, ft2).features.map(f => f.geometry.coordinates));
      }
      return null
   }
   if (ft1.type === "LineString" && ft2.type === "MultiLineString") {
      if (booleanIntersects(ft1, ft2)) {
         let intersectingPoints = ft2.coordinates.map(line => lineIntersect(ft1, { type: "LineString", coordinates: line }).features.map(f => f.geometry.coordinates)).flat();
         return multiPoint(intersectingPoints);
      }
      return null;
   }
   if (ft1.type === "MultiLineString" && ft2.type === "LineString") {
      return intersectionLine(ft2, ft1);
   }
   if (ft1.type === "MultiLineString" && ft2.type === "MultiLineString") {
      let intersectingPoints: number[][] = [];
      for (let line1 of ft1.coordinates) {
         for (let line2 of ft2.coordinates) {
            if (booleanIntersects({ type: "LineString", coordinates: line1 }, { type: "LineString", coordinates: line2 })) {
               intersectingPoints.push(...lineIntersect({ type: "LineString", coordinates: line1 }, { type: "LineString", coordinates: line2 }).features.map(f => f.geometry.coordinates));
            }
         }
      }
      return intersectingPoints.length > 0 ? multiPoint(intersectingPoints) : null;
   }
   if (ft1.type === "LineString" && (ft2.type === "Polygon" || ft2.type === "MultiPolygon")) {
      let splits = splitLineByPolygon(ft1, ft2);
      console.log(splits);
      let intersectingLinessegments = splits.filter(f => booleanIntersects(center(f), feature(ft2))).map(f => f.geometry.coordinates);
      if (ft1.coordinates.every(coord => intersectionPoint({ type: "Point", coordinates: coord }, ft2))) {
         intersectingLinessegments.push(ft1.coordinates);
      }
      if (intersectingLinessegments.length === 0) {
         return null;
      } else if (
         intersectingLinessegments.length === 1
      ) {
         return lineString(intersectingLinessegments[0]);
      }
      return multiLineString(intersectingLinessegments);
   }
   if (ft1.type === "MultiLineString" && (ft2.type === "Polygon" || ft2.type === "MultiPolygon")) {
      let intersectingLinessegments: MultiLineString["coordinates"] = [];
      for (let line of ft1.coordinates) {
         let lineFt = feature({ type: "LineString", coordinates: line })
         let splits = lineSplit(lineFt, feature(ft2));
         intersectingLinessegments.push(...splits.features.filter(f => booleanIntersects(center(f), feature(ft2))).map(f => f.geometry.coordinates));
         if (line.every(coord => intersectionPoint({ type: "Point", coordinates: coord }, ft2))) {
            intersectingLinessegments.push(line);
         }
      }
      if (intersectingLinessegments.length === 0) {
         return null;
      } else if (intersectingLinessegments.length === 1) {
         return lineString(intersectingLinessegments[0]);
      }
      return multiLineString(intersectingLinessegments);
   }
   if (ft1.type === "Polygon" || ft1.type === "MultiPolygon") {
      if (ft2.type === "LineString" || ft2.type === "MultiLineString") {
         return intersectionLine(ft2, ft1);
      }
   }
   return null;
}

function splitLineByPolygon(ft1: GeoJSON.LineString, ft2: GeoJSON.Polygon | GeoJSON.MultiPolygon): GeoJSON.Feature<GeoJSON.LineString>[] {
   let segments = lineSegment(ft2).features;
   let split = lineSplit(feature(ft1), multiLineString(segments.map(s => s.geometry.coordinates)));
   return split.features;
}

function intersectionPolygon(ft1: GeoJSON.Geometry, ft2: GeoJSON.Geometry): GeoJSON.Feature | null {
   if (
      ft1.type === "Point"
      || ft2.type === "Point"
      || ft1.type === "MultiPoint"
      || ft2.type === "MultiPoint"
      || ft1.type === "LineString"
      || ft2.type === "LineString"
      || ft1.type === "MultiLineString"
      || ft2.type === "MultiLineString"
      || ft1.type === "GeometryCollection"
      || ft2.type === "GeometryCollection"
   ) {
      return intersectionLine(ft1, ft2);
   }
   return intersect(featureCollection([feature(ft1), feature(ft2)]))
}

function intersectFeature(ft1: GeoJSON.Feature, ft2: GeoJSON.Feature): GeoJSON.Feature | null {
   return intersectionPolygon(ft1.geometry, ft2.geometry);
}

function intersectionFeatureCollection(fc1: GeoJSON.FeatureCollection | null, fc2: GeoJSON.FeatureCollection | null): GeoJSON.FeatureCollection | null {
   if (!fc1 || !fc2) {
      return null;
   }
   let intersections = fc1.features.map((a) => {
      return fc2.features.map((b) => {
         return intersectFeature(a, b);
      });
   }).flat().filter(f => f !== null) as GeoJSON.Feature[];
   return intersections.length > 0 ? featureCollection(intersections) : null;
}

export function conflictMeasurementDisplay(number: number, geom: GeoJSON.FeatureCollection | null) {
   if (!geom || geom.features.length === 0) return "-";
   switch (geom.features[0].geometry.type) {
      case "Polygon":
      case "MultiPolygon":
         return area(number);
      case "LineString":
      case "MultiLineString":
         return length(number);
      default:
         return ".";
   }

   // Removed as handled in the switch statement above
}


function length(lengthKM: number) {
   if (lengthKM < 1) return Intl.NumberFormat(Localization.getLocale(), { maximumFractionDigits: 0 }).format(lengthKM * 1000) + " m";
}

function area(areaM2: number) {
   if (areaM2 < 0) return "-";
   return Intl.NumberFormat(Localization.getLocale(), { maximumFractionDigits: 0 }).format(areaM2) + " m²";
}